import React, { useRef, useState } from 'react';
import { Form, Avatar, Button } from 'antd';
import { CheckOutlined, UploadOutlined, UserOutlined, DeleteOutlined } from '@ant-design/icons';
import { getMediaURL } from '../../context/API';
import {
  EditingCard,
  EditingBadge,
  FormSection,
  StyledInput,
  SaveChangesButton,
  CancelButton,
  ActionButtons,
} from './Styles';
import { Typography } from 'antd';
import type { ProfileEditFormProps } from './types';

const ProfileEditForm: React.FC<ProfileEditFormProps> = ({
  formData,
  logo,
  loading,
  onInputChange,
  onLogoChange,
  onSave,
  onCancel,
}) => {
  const [previewLogo, setPreviewLogo] = useState<string | null>(logo ? getMediaURL(logo) || null : null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }

      onLogoChange(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewLogo(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setPreviewLogo(null);
    onLogoChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <EditingCard>
      <EditingBadge>Editing Profile</EditingBadge>
      <Typography.Title level={4} style={{ marginBottom: 24 }}>
        Edit Profile Information
      </Typography.Title>
      <FormSection>
        <Form.Item label="Logo">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
            <Avatar
              size={80}
              src={previewLogo || undefined}
              icon={<UserOutlined />}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                id="logo-upload"
              />
              <label htmlFor="logo-upload">
                <Button
                  icon={<UploadOutlined />}
                  onClick={() => fileInputRef.current?.click()}
                  style={{ marginRight: 8 }}
                >
                  Upload Logo
                </Button>
              </label>
              {(previewLogo || logo) && (
                <Button
                  icon={<DeleteOutlined />}
                  danger
                  onClick={handleRemoveLogo}
                >
                  Remove Logo
                </Button>
              )}
            </div>
          </div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Recommended: Square image, max 5MB. JPG, PNG, or GIF format.
          </Typography.Text>
        </Form.Item>
        <Form.Item label="Full Name">
          <StyledInput
            name="name"
            value={formData.name}
            onChange={onInputChange}
            placeholder="Enter your full name"
          />
        </Form.Item>
        <Form.Item label="Username">
          <StyledInput
            name="username"
            value={formData.username}
            onChange={onInputChange}
            placeholder="Enter your username"
          />
        </Form.Item>
        <Form.Item label="Business Address">
          <StyledInput
            name="address"
            value={formData.address}
            onChange={onInputChange}
            placeholder="Enter your business address"
          />
        </Form.Item>
        <ActionButtons>
          <SaveChangesButton icon={<CheckOutlined />} onClick={onSave} loading={loading}>
            Save Changes
          </SaveChangesButton>
          <CancelButton onClick={onCancel}>
            Cancel
          </CancelButton>
        </ActionButtons>
      </FormSection>
    </EditingCard>
  );
};

export default React.memo(ProfileEditForm);

